import React, { useState, useRef } from 'react';
import Slider from 'react-slick';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BannerSlide } from '../../types/bannerSlides';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface BannerSliderProps {
  slides: BannerSlide[];
  autoSlideInterval?: number;
}

const BannerSlider: React.FC<BannerSliderProps> = ({
  slides,
  autoSlideInterval = 5000,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>(
    {}
  );
  const sliderRef = useRef<Slider | null>(null);

  if (!slides.length) return null;

  const handleImageError = (slideId: string) => {
    setImageErrors(prev => ({ ...prev, [slideId]: true }));
  };

  const handleBannerClick = (bannerUrl: string) => {
    if (bannerUrl && bannerUrl.trim() !== '') {
      // Check if the URL is external (starts with http:// or https://)
      if (bannerUrl.startsWith('http://') || bannerUrl.startsWith('https://')) {
        // Open external URL in new tab
        window.open(bannerUrl, '_blank', 'noopener,noreferrer');
      } else {
        // Navigate to internal route
        navigate(bannerUrl);
      }
    }
  };

  // Get language-specific URL
  const getBannerUrl = (slide: BannerSlide): string => {
    const currentLanguage = i18n.language;
    
    switch (currentLanguage) {
      case 'zh':
        return slide.bannerUrlZh || slide.bannerUrl;
      case 'cn':
        return slide.bannerUrlCn || slide.bannerUrl;
      default:
        return slide.bannerUrl;
    }
  };

  // Get language-specific title
  const getBannerTitle = (slide: BannerSlide): string => {
    const currentLanguage = i18n.language;
    
    switch (currentLanguage) {
      case 'zh':
        return slide.titleZh || slide.title;
      case 'cn':
        return slide.titleCn || slide.title;
      default:
        return slide.title;
    }
  };

  // Responsive image with language support
  const getImageUrl = (slide: BannerSlide) => {
    const currentLanguage = i18n.language;
    const isMobileView = typeof window !== 'undefined' && window.innerWidth < 600;
    
    if (isMobileView) {
      // Mobile image
      switch (currentLanguage) {
        case 'zh':
          return slide.mobileImageUrlZh || slide.mobileImageUrl;
        case 'cn':
          return slide.mobileImageUrlCn || slide.mobileImageUrl;
        default:
          return slide.mobileImageUrl;
      }
    } else {
      // Desktop image
      switch (currentLanguage) {
        case 'zh':
          return slide.desktopImageUrlZh || slide.desktopImageUrl;
        case 'cn':
          return slide.desktopImageUrlCn || slide.desktopImageUrl;
        default:
          return slide.desktopImageUrl;
      }
    }
  };

  // If only one banner, show single image instead of slider
  if (slides.length === 1) {
    const slide = slides[0];
    const bannerUrl = getBannerUrl(slide);
    const bannerTitle = getBannerTitle(slide);
    const isClickable = bannerUrl && bannerUrl.trim() !== '';
    
    return (
      <Box sx={{ width: '100%', mb: isMobile ? 0 : 4, mx: { xs: 0, sm: -1 } }}>
        <div style={{ padding: '0 8px' }}>
          <Box
            onClick={() => handleBannerClick(bannerUrl)}
            style={{ 
              display: 'block', 
              width: '100%',
              cursor: isClickable ? 'pointer' : 'default'
            }}
            tabIndex={isClickable ? 0 : -1}
            role={isClickable ? 'button' : undefined}
            onKeyDown={(e) => {
              if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleBannerClick(bannerUrl);
              }
            }}
          >
            <img
              src={
                imageErrors[slide.id.toString()]
                  ? '/images/img_placeholder.svg'
                  : getImageUrl(slide)
              }
              alt={bannerTitle}
              onError={() => handleImageError(slide.id.toString())}
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'cover',
                borderRadius: isMobile ? '0px' : '16px',
                transition: 'transform 0.3s',
                transform: isClickable ? 'scale(1)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (isClickable) {
                  e.currentTarget.style.transform = 'scale(1.02)';
                }
              }}
              onMouseLeave={(e) => {
                if (isClickable) {
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            />
          </Box>
        </div>
      </Box>
    );
  }

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: autoSlideInterval,
    arrows: false,
    pauseOnHover: true,
    adaptiveHeight: false,
    centerMode: true,
    centerPadding: '15%', // 60% main, 20% left, 20% right
    afterChange: (index: number) => setCurrentSlide(index),
    responsive: [
      {
        breakpoint: 900,
        settings: {
          centerPadding: '10%',
        },
      },
      {
        breakpoint: 600,
        settings: {
          centerMode: false,
          centerPadding: '0',
        },
      },
    ],
  };

  return (
    <Box sx={{ width: '100%', mb: isMobile ? 0 : 4, mx: { xs: 0, sm: -1 } }}>
      {/* Remove focus outline from slider images */}
      <style>{isMobile ? `
        .slick-slide a:focus, .slick-slide a:active {
          outline: none !important;
          box-shadow: none !important;
        }
      ` : `
        .slick-slide a:focus, .slick-slide a:active {
          outline: none !important;
          box-shadow: none !important;
        }

        .slick-slide > div {
          padding: 0 5px;
        }
      `}</style>
      <Slider ref={sliderRef} {...settings}>
        {slides.map(slide => {
          const bannerUrl = getBannerUrl(slide);
          const bannerTitle = getBannerTitle(slide);
          const isClickable = bannerUrl && bannerUrl.trim() !== '';
          
          return (
            <div key={slide.id} style={{ padding: '0 8px' }}>
              <Box
                onClick={() => handleBannerClick(bannerUrl)}
                style={{ 
                  display: 'block', 
                  width: '100%',
                  cursor: isClickable ? 'pointer' : 'default'
                }}
                tabIndex={isClickable ? 0 : -1}
                role={isClickable ? 'button' : undefined}
                onKeyDown={(e) => {
                  if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleBannerClick(bannerUrl);
                  }
                }}
              >
                <img
                  src={
                    imageErrors[slide.id.toString()]
                      ? '/images/img_placeholder.svg'
                      : getImageUrl(slide)
                  }
                  alt={bannerTitle}
                  onError={() => handleImageError(slide.id.toString())}
                  style={{
                    width: '100%',
                    objectFit: 'cover',
                    borderRadius: isMobile ? '0px' : '16px',
                    transition: 'transform 0.3s',
                    transform: isClickable ? 'scale(1)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (isClickable) {
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isClickable) {
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                />
              </Box>
            </div>
          );
        })}
      </Slider>
      {/* Custom pagination with arrows - Desktop Only */}
        <Box
          sx={{
            display: isMobile ? 'none' : 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 4,
            mt: 1,
          }}
        >
        {/* Left Arrow */}
        <Box
          component="button"
          onClick={() => sliderRef.current?.slickPrev()}
          sx={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            p: 0,
            display: 'flex',
            alignItems: 'center',
            height: 24,
            width: 24,
            justifyContent: 'center',
          }}
          aria-label="Previous slide"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 8L12 16L20 24"
              stroke="#373331"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
        {/* Indicator */}
        <Box
          sx={{
            background: '#E7E3D5',
            borderRadius: '99px',
            width: '64px',
            height: '24px',
            px: 4,
            py: 1,
            fontSize: 14,
            fontWeight: 400,
            color: '#373331',
            textAlign: 'center',
            fontFamily: 'inherit',
            letterSpacing: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {`${currentSlide + 1}/${slides.length}`}
        </Box>
        {/* Right Arrow */}
        <Box
          component="button"
          onClick={() => sliderRef.current?.slickNext()}
          sx={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            p: 0,
            display: 'flex',
            alignItems: 'center',
            height: 24,
            width: 24,
            justifyContent: 'center',
          }}
          aria-label="Next slide"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 8L20 16L12 24"
              stroke="#373331"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

export default BannerSlider;
